import type { App } from 'vue';
import { createVueIsland } from '@/createVueIsland';
import AccountingModeChangeDialog from '@/modules/matters/components/AccountingModeChangeDialog.vue';
import {
  billClientChangeDecision,
  type BillClientChangeContext,
  type AccountingModeDialogParams,
} from '@/modules/matters/matterBillingRules';

export { billClientChangeErrorMessage } from '@/modules/matters/matterBillingRules';
export type { BillClientChangeContext } from '@/modules/matters/matterBillingRules';

/**
 * The slice of the dialog this guard drives. It constrains the call site but cannot
 * be tied back to the component's `defineExpose`: `src/shims-vue.d.ts` declares every
 * `.vue` as `DefineComponent<{}, {}, any>`, so `InstanceType<typeof Dialog>` accepts
 * any member and checks nothing. AccountingModeChangeDialog.spec.ts covers the other
 * half — that the component still exposes `open`.
 */
interface DialogVm {
  open: (params: AccountingModeDialogParams) => Promise<boolean>;
}

export interface AccountingModeGuard {
  /**
   * `true` when the accounting-mode change may be applied — no settlement blocks
   * it, or the user reset the one that did. Rejects when the settlement lookup
   * fails, so callers fail closed; map the rejection with
   * `billClientChangeErrorMessage` before showing it.
   */
  check: (context: BillClientChangeContext) => Promise<boolean>;
  /** Unmounts the dialog island. Idempotent; safe before any mount happened. */
  destroy: () => void;
}

/**
 * The accounting-mode change guard, shared by the two places that write the
 * setting: the matter Billing tab toggle and the QuickBooks Connector modal.
 *
 * Owns the lazily-mounted dialog island and its teardown, and collapses
 * `billClientChangeDecision`'s two callbacks into one boolean. The decision
 * itself lives in matterBillingRules.ts and is not altered here.
 *
 * Callers keep their own `$destroy` bookkeeping — this cannot know when an
 * Angular scope has gone away, only that it should stop mounting once told.
 */
export function createAccountingModeGuard(): AccountingModeGuard {
  let app: App | null = null;
  let vm: DialogVm | null = null;
  let host: HTMLElement | null = null;
  let destroyed = false;

  function dialog(): DialogVm {
    if (vm) return vm;
    // Created here, not declared in a template: views/ is frozen (AGENTS.md).
    host = document.createElement('div');
    document.body.appendChild(host);
    app = createVueIsland(AccountingModeChangeDialog);
    vm = app.mount(host) as unknown as DialogVm;
    return vm;
  }

  async function check(context: BillClientChangeContext): Promise<boolean> {
    let allowed = false;
    await billClientChangeDecision(context, {
      openDialog: (params) => {
        if (destroyed) return Promise.resolve(false); // don't mount after teardown
        return dialog().open(params);
      },
      // The callers apply the change themselves — one writes it through the API
      // immediately, the other lets a pending Save proceed — so this records
      // that the guard cleared it rather than performing it.
      performBillClientChange: () => {
        allowed = true;
      },
    });
    return allowed;
  }

  function destroy(): void {
    destroyed = true;
    // No `if (!app) return` guard: when createVueIsland or mount throws, the host is
    // already in the document while app is still null, and an early return orphaned
    // that div for the session. Optional calls make this idempotent instead.
    try {
      app?.unmount();
    } catch {
      // Teardown only — nothing actionable, and logging the error object risks
      // leaking response detail into the console (security.md §3).
    } finally {
      app = null;
      vm = null;
      host?.remove();
      host = null;
    }
  }

  return { check, destroy };
}
